/**
 * 문항 카드 AI 액션 스펙 테이블 — 액션별로 갈리는 것은 여기 일곱 열이 전부다.
 *
 *   feature      플랜 게이트          eligibility  실행 직전 재검사(메뉴도 같은 함수를 쓴다)
 *   revisionFor  "응답이 왔을 때 입력이 그대로인가" 지문 팩토리   slotAtom  스켈레톤/에러 자리
 *   minVisibleMs 최소 스켈레톤 노출  request  요청 본문 조립   apply  응답 → 폼
 *
 * 실행 순서(가드 → 잠금 → 요청 → 리비전 가드 → 적용 → 계측)는 useQuestionCardAiActions 가
 * 액션과 무관하게 한 경로로 돈다. 새 액션은 이 테이블에 행 하나를 더하는 일이다.
 *
 * `request` 는 (input, quiz) 만 받는다 — index 를 넘길 길이 없다. index 는 적용 시점에
 * runner 가 id 로 다시 찾은 ActionTarget 안에서만 존재한다(선지 index 도 그 quiz 에서 slot id 로 푼다).
 */
import { type PrimitiveAtom } from 'jotai';

import { type PlanFeatureType } from '@/shared/plan/consts';

import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { MIN_SKELETON_MS } from '../consts/questionCardAi.const';
import {
  fillSlotAtom,
  type QuestionCardAiSlots,
  similarSlotAtom,
} from '../stores/questionCardAi.atoms';
import {
  type ActionRequestInput,
  type ActionTarget,
  type ApplyOutcome,
  type Eligibility,
  type FormWriter,
  type RunnableQuestionCardAiAction,
} from '../types/questionCardAi.types';

import { applyFillOptionsResponse, fillRevisionFor } from './fillOptions';
import {
  resolveFillOptionsEligibility,
  resolveSimilarEligibility,
} from './questionCardAiEligibility';
import {
  type FillOptionsResponseDto,
  type SimilarQuestionResponseDto,
  type SimilarQuestionVariant,
} from './questionCardAiEndpoints';
import { type RevisionOf, similarRevision } from './questionCardAiRevision';
import { slotIdsOf } from './questionCardAiSlotSchema';
import {
  requestFillOptions,
  requestSimilarQuestion,
} from './questionCardAiTransport';
import { applySimilarQuestionResponse } from './similarQuestion';

type ActionDefinition<Res extends { model: string }> = {
  feature: PlanFeatureType;
  slotAtom: PrimitiveAtom<QuestionCardAiSlots>;
  /** 실패를 어디에 보이나. 'slot' = 슬롯 error 상태(카드 자리), 'alert' = 슬롯을 비우고 알럿 모달. */
  failure: 'slot' | 'alert';
  eligibility: (target: Pick<ActionTarget, 'quiz' | 'count'>) => Eligibility;
  /**
   * "응답이 왔을 때 입력이 그대로인가" 지문 팩토리. 요청 직전에 `requested` 로 한 번 호출해
   * 비교 기준을 **그 자리에서** 굳히고, 돌려받은 함수로 요청 시점·적용 시점 지문을 만든다.
   * (폼 객체는 setValue 로 제자리 변경되므로 나중에 "요청 시점 문항" 을 다시 읽을 수 없다.)
   */
  revisionFor: (requested: BuildQuizListItem) => RevisionOf;
  /** 결과가 너무 빨리 오면 스켈레톤이 깜빡이기만 한다. 없으면 즉시 적용. */
  minVisibleMs?: number;
  request: (input: ActionRequestInput, quiz: BuildQuizListItem) => Promise<Res>;
  apply: (
    response: Res,
    target: ActionTarget,
    writer: FormWriter,
  ) => ApplyOutcome;
};

/** runner 가 보는 모양. 응답 타입은 apply 클로저에 갇혀 테이블이 이종 타입을 섞어도 안전하다. */
export type QuestionCardAiActionSpec = Omit<
  ActionDefinition<never>,
  'request' | 'apply'
> & {
  run: (
    input: ActionRequestInput,
    quiz: BuildQuizListItem,
  ) => Promise<{
    model: string;
    apply: (target: ActionTarget, writer: FormWriter) => ApplyOutcome;
  }>;
};

const defineAction = <Res extends { model: string }>({
  request,
  apply,
  ...rest
}: ActionDefinition<Res>): QuestionCardAiActionSpec => ({
  ...rest,
  run: async (input, quiz) => {
    const response = await request(input, quiz);
    return {
      model: response.model,
      apply: (target, writer) => apply(response, target, writer),
    };
  },
});

const similarQuestion = (variant: SimilarQuestionVariant) =>
  defineAction<SimilarQuestionResponseDto>({
    feature: 'AI_SIMILAR_QUIZ_GENERATION',
    slotAtom: similarSlotAtom,
    failure: 'slot',
    eligibility: resolveSimilarEligibility,
    // 입력이 하나라도 바뀌면 결과가 무의미하다 — 실제 요청 본문 전체가 지문이다.
    revisionFor: () => similarRevision,
    minVisibleMs: MIN_SKELETON_MS,
    request: input => requestSimilarQuestion({ ...input, variant }),
    apply: applySimilarQuestionResponse,
  });

export const QUESTION_CARD_AI_ACTION_SPECS = {
  similar: similarQuestion('similar'),
  harder: similarQuestion('harder'),
  fill_options: defineAction<FillOptionsResponseDto>({
    feature: 'AI_CHOICE_GENERATION',
    slotAtom: fillSlotAtom,
    failure: 'alert',
    eligibility: resolveFillOptionsEligibility,
    // 빈 슬롯 편집은 허용해야 한다 — 요청 시점에 값이 있던 슬롯과 정답 자리만 지문이다.
    revisionFor: fillRevisionFor,
    request: (input, quiz) =>
      requestFillOptions({ ...input, slotIds: slotIdsOf(quiz) }),
    apply: applyFillOptionsResponse,
  }),
} satisfies Record<RunnableQuestionCardAiAction, QuestionCardAiActionSpec>;
